import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Download, Save, ArrowLeft, CheckCircle, Edit2, Loader2, AlertTriangle } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

/**
 * Live Agreement Form
 * Shows the template with actual data pulled from approved proposal
 * Allows editing of specific fields (notes, agency, contact info)
 * Saves agreement and generates PDF matching template exactly
 */
export default function SaasAgreementForm() {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [template, setTemplate] = useState(getDefaultTemplate()); // Start with default template
  
  // Editable fields
  const [endUserName, setEndUserName] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [functionSelections, setFunctionSelections] = useState({});
  const [termLength, setTermLength] = useState(1); // NEW: 1, 3, or 5 years
  
  // Signature fields
  const [principalName, setPrincipalName] = useState('');
  const [principalTitle, setPrincipalTitle] = useState('');
  const [principalDate, setPrincipalDate] = useState('');
  const [endUserSignatureName, setEndUserSignatureName] = useState('');
  const [endUserSignatureTitle, setEndUserSignatureTitle] = useState('');
  const [endUserSignatureDate, setEndUserSignatureDate] = useState('');
  const [achData, setAchData] = useState({
    ein: '', website: '', ach_services: [],
    principal_name: '', principal_title: '', principal_date: '',
    client_name: '', client_title: '', client_date: ''
  });
  const [crofData, setCrofData] = useState({
    sales_person: '', requestor: '', shipping_address: '',
    contact_phone: '', contact_email: '', notes: '',
    serial_numbers: '', tracking_number: '', date_of_delivery: '',
    selected_option: 1, reader_entries: [], total_annual_cost: 0, rate_increase_pct: 0,
    customer_signature_name: '', customer_signature_date: ''
  });

  // Implementation Checklist Data (Form 4 of 4)
  const [implChecklist, setImplChecklist] = useState({
    // Required fields
    go_live_date: '',
    go_live_type: 'specific', // 'specific', 'range', 'na'
    go_live_range_start: '',
    go_live_range_end: '',
    integration_notes: '',
    estore_categories: '',
    w9_received: false,
    voided_check_received: false, // NEW: For ACH setup
    bank_letter_received: false,  // NEW: Alternative to voided check
    special_requests: '',
    // IT & Technical Contact
    customer_title: '',
    it_contact_name: '',
    it_contact_email: '',
    it_contact_phone: '',
    public_ip_address: '',
    // Card Reader Order (Section 4)
    card_reader_notes: '',
    // Billing & Deposit Structure (Section 8)
    allow_partial_payments: null,
    allow_overpayments: null,
    custom_payment_options: [], // Array of {label, enabled}
    deposit_frequency: '', // 'daily', 'weekly', 'monthly', 'custom'
    deposit_structure: '',
    mid_setup_notes: '',
    billing_notes: '',
    // Permissions & Operations (Section 9)
    allow_clerk_void: null,
    allow_clerk_refund: null,
    allow_phone_payments: null,
    custom_permission_options: [], // Array of {label, enabled}
    daily_reports_needed: '',
    notifications_needed: '',
    permissions_notes: '',
    // User Accounts (Section 10)
    user_accounts: [], // Array of {name, email, role, permissions}
    user_accounts_notes: '',
    // Branding Assets (Section 11)
    logo_received: false,
    logo_notes: '',
    header_image_received: false,
    header_image_notes: '',
    branding_colors: '',
    branding_notes: '',
    // Training & Onboarding (Section 12) - NEW
    training_scheduled: false,
    training_date: '',
    training_attendees: '',
    training_format: '', // 'in-person', 'remote', 'hybrid', 'self-service'
    training_notes: '',
    support_contact_provided: false
  });
  
  // Formatting options
  const [useBulletFormat, setUseBulletFormat] = useState(true); // Default to bullets
  
  // Bullet formatting options for Implementation Checklist fields (loaded from template)
  const [implBulletFormats, setImplBulletFormats] = useState({
    integrationNotes: false,
    estoreCategories: false,
    specialRequests: true,
    depositStructure: false,
    midSetupNotes: false,
    dailyReports: true,
    notifications: true,
    userAccountsNotes: false,
    logoNotes: false,
    headerImageNotes: false,
    brandingNotes: false
  });

  // Section visibility options for Implementation Checklist (loaded from template)
  const [implSectionVisibility, setImplSectionVisibility] = useState({
    customerInfo: true,
    goLiveDate: true,
    servicesRates: true,
    cardReaderOrder: true,
    requiredDocuments: true,
    specialRequests: true,
    itContact: true,
    billingDeposit: true,
    permissions: true,
    userAccounts: true,
    brandingAssets: true,
    trainingOnboarding: true // NEW: Section 12
  });

  // Helper function to check if an Implementation Checklist section is visible
  const isSectionVisible = (sectionKey) => {
    return implSectionVisibility[sectionKey] !== false;
  };

  // Reader types from settings (auto-populates CROF unit prices)
  const [readerTypesMap, setReaderTypesMap] = useState({});

  useEffect(() => {
    if (collectionId) {
      loadData();
    }
  }, [collectionId]);

  // Load reader types from settings on mount (idempotent — read-only)
  useEffect(() => {
    api.get('/reader-types-full')
      .then(res => {
        const map = {};
        (res.data?.reader_types || []).forEach(rt => {
          map[rt.name] = {
            monthly_cost: rt.monthly_cost || 0,
            sim_cost: rt.sim_cost || 0,
            has_sim: !!rt.has_sim,
          };
        });
        setReaderTypesMap(map);
      })
      .catch(err => console.warn('Failed to load reader types:', err));
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load template configuration
      const templateRes = await api.get('/agreements/template-config');
      // Use backend config if it exists and has the right structure, otherwise use default
      if (templateRes.data?.config?.scheduleA) {
        const backendConfig = templateRes.data.config;
        
        // CRITICAL FIX: Ensure the 'function' column always exists in scheduleA
        // The backend template may be missing this column, causing W/P/I checkboxes to disappear
        const functionColumn = backendConfig.scheduleA.columns.find(col => col.id === 'function');
        
        if (!functionColumn) {
          console.log('⚠️ Backend template missing function column - injecting it');
          // Find the position after 'module' or 'service' column
          const moduleIndex = backendConfig.scheduleA.columns.findIndex(
            col => col.id === 'module' || col.id === 'service'
          );
          const insertIndex = moduleIndex >= 0 ? moduleIndex + 1 : 0;
          
          // Insert the function column
          backendConfig.scheduleA.columns.splice(insertIndex, 0, {
            id: 'function',
            label: 'Function',
            visible: true
          });
          
          console.log('✅ Function column injected at position', insertIndex);
        }

        // CRITICAL FIX: Ensure termsAndConditions content is preserved from DB.
        // If backendConfig is missing or has empty T&C, do NOT fall back to empty default.
        if (!backendConfig.termsAndConditions?.content) {
          console.warn('⚠️ Backend template has empty termsAndConditions - using default template structure but keeping other backend fields');
        }
        
        // Load Implementation Checklist bullet formatting options from template
        if (backendConfig.implementationChecklist?.bulletFormatting) {
          const bf = backendConfig.implementationChecklist.bulletFormatting;
          setImplBulletFormats(prev => ({
            ...prev,
            integrationNotes: bf.integrationNotes?.enabled || false,
            estoreCategories: bf.estoreCategories?.enabled || false,
            specialRequests: bf.specialRequests?.enabled !== false, // Default true
            depositStructure: bf.depositStructure?.enabled || false,
            midSetupNotes: bf.midSetupNotes?.enabled || false,
            dailyReports: bf.dailyReports?.enabled !== false, // Default true
            notifications: bf.notifications?.enabled !== false, // Default true
            userAccountsNotes: bf.userAccountsNotes?.enabled || false,
            logoNotes: bf.logoNotes?.enabled || false,
            headerImageNotes: bf.headerImageNotes?.enabled || false,
            brandingNotes: bf.brandingNotes?.enabled || false,
          }));
        }
        
        // Load Implementation Checklist section visibility options from template
        if (backendConfig.implementationChecklist?.sections) {
          const sections = backendConfig.implementationChecklist.sections;
          setImplSectionVisibility(prev => ({
            ...prev,
            customerInfo: sections.customerInfo?.enabled !== false,
            goLiveDate: sections.goLiveDate?.enabled !== false,
            servicesRates: sections.servicesRates?.enabled !== false,
            cardReaderOrder: sections.cardReaderOrder?.enabled !== false,
            requiredDocuments: sections.requiredDocuments?.enabled !== false,
            specialRequests: sections.specialRequests?.enabled !== false,
            itContact: sections.itContact?.enabled !== false,
            billingDeposit: sections.billingDeposit?.enabled !== false,
            permissions: sections.permissions?.enabled !== false,
            userAccounts: sections.userAccounts?.enabled !== false,
            brandingAssets: sections.brandingAssets?.enabled !== false,
            trainingOnboarding: sections.trainingOnboarding?.enabled !== false,
          }));
        }
        
        setTemplate(backendConfig);
      } else {
        console.log('Using default template - backend template not found or incomplete');
        setTemplate(getDefaultTemplate());
      }
      
      // Load proposal data
      const proposalRes = await api.get(`/collections/${collectionId}`);
      setProposal(proposalRes.data);
      
      // Try to load existing agreement
      try {
        const agreementRes = await api.get(`/agreements/collection/${collectionId}`);
        setAgreement(agreementRes.data);
        
        // Pre-fill editable fields from saved agreement
        setEndUserName(agreementRes.data.end_user_name || proposalRes.data.client_name || '');
        setAgencyName(agreementRes.data.agency_name || proposalRes.data.agency_name || '');
        setContactName(agreementRes.data.contact_name || proposalRes.data.contact_name || '');
        setContactPhone(agreementRes.data.contact_phone || proposalRes.data.contact_phone || '');
        setAddress(agreementRes.data.address || proposalRes.data.client_address || '');
        setNotes(agreementRes.data.notes || '');
        setFunctionSelections(agreementRes.data.function_selections || {});
        setTermLength(agreementRes.data.term_length || 1); // NEW: Load term length
        
        // Pre-fill signature fields from saved agreement
        setPrincipalName(agreementRes.data.principal_name || '');
        setPrincipalTitle(agreementRes.data.principal_title || '');
        setPrincipalDate(agreementRes.data.principal_date || '');
        setEndUserSignatureName(agreementRes.data.end_user_signature_name || '');
        setEndUserSignatureTitle(agreementRes.data.end_user_signature_title || '');
        setEndUserSignatureDate(agreementRes.data.end_user_signature_date || '');
        
        // Pre-fill formatting options
        setUseBulletFormat(agreementRes.data.use_bullet_format !== undefined ? agreementRes.data.use_bullet_format : true);

        // Pre-fill ACH and CROF data
        if (agreementRes.data.ach_data) setAchData(agreementRes.data.ach_data);
        if (agreementRes.data.crof_data) setCrofData(agreementRes.data.crof_data);
        
        // Pre-fill Implementation Checklist data
        if (agreementRes.data.impl_checklist) {
          setImplChecklist(prev => ({ ...prev, ...agreementRes.data.impl_checklist }));
        }
      } catch (err) {
        // No existing agreement, pre-fill from proposal
        setEndUserName(proposalRes.data.client_name || '');
        setAgencyName(proposalRes.data.agency_name || '');
        setContactName(proposalRes.data.contact_name || '');
        setContactPhone(proposalRes.data.contact_phone || '');
        setAddress(proposalRes.data.client_address || '');
        setNotes('');
        
        // Initialize function selections for each service
        const initialSelections = {};
        proposalRes.data.scenarios?.forEach(scenario => {
          initialSelections[scenario.id] = {
            W: false,
            P: false,
            I: false
          };
        });
        setFunctionSelections(initialSelections);
        
        // ── PHASE 2 TASK 2: ENHANCED AUTO-POPULATION ──────────────────────
        // Auto-populate Implementation Checklist fields from proposal data
        const autoPopulatedChecklist = {};
        
        // Auto-populate discovery date (use proposal creation date or approval date)
        if (proposalRes.data.created_at) {
          autoPopulatedChecklist.discovery_date = proposalRes.data.created_at.split('T')[0]; // Format: YYYY-MM-DD
        }
        
        // Auto-populate expected transaction volume from scenarios
        if (proposalRes.data.scenarios?.length) {
          const totalVolume = proposalRes.data.scenarios.reduce((sum, s) => {
            return sum + (s.scenario_info?.expected_monthly_transactions || 0);
          }, 0);
          if (totalVolume > 0) {
            autoPopulatedChecklist.expected_transaction_volume = `${totalVolume.toLocaleString()} transactions/month (estimated from proposal)`;
          }
        }
        
        // Auto-populate in-scope services from proposal
        if (proposalRes.data.scenarios?.length) {
          const serviceNames = proposalRes.data.scenarios.map(s => s.service_name || s.name).filter(Boolean);
          if (serviceNames.length > 0) {
            autoPopulatedChecklist.in_scope_services = serviceNames.join(', ');
          }
        }
        
        // Auto-populate target go-live date (default: 60 days from now)
        const targetDate = new Date();
        targetDate.setDate(targetDate.getDate() + 60); // Standard 60-day implementation
        autoPopulatedChecklist.target_go_live = targetDate.toISOString().split('T')[0];
        
        // Auto-populate implementation phase (for new agreements)
        autoPopulatedChecklist.implementation_phase = 'not_started';
        
        // Set the auto-populated checklist data
        setImplChecklist(prev => ({ ...prev, ...autoPopulatedChecklist }));
        // ── END ENHANCED AUTO-POPULATION ──────────────────────────────────
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load agreement data');
    } finally {
      setLoading(false);
    }
  };

  // Compute current CROF reader entries (used by save handler so persisted values match settings)
  const currentCrofEntries = () => {
    const rdrSvcs = (proposal?.scenarios || []).filter(s =>
      s.scenario_info?.readersQty > 0 && s.scenario_info?.readerType && s.scenario_info?.readerType !== 'N/A'
    );
    return rdrSvcs.map(s => {
      const rtype = s.scenario_info?.readerType;
      const qty = s.scenario_info?.readersQty || 0;
      const settings = readerTypesMap[rtype] || {};
      const monthly = settings.monthly_cost != null ? settings.monthly_cost : 0;
      return {
        service_name: s.service_name,
        reader_type: rtype,
        quantity: qty,
        monthly_unit_cost: monthly,
        annual_cost: monthly * qty * 12,
      };
    });
  };

  const handleSaveAgreement = async () => {
    try {
      setSaving(true);

      // Compute up-to-date reader entries from settings so the saved CROF persists current pricing
      const computedEntries = currentCrofEntries();
      const computedTotalAnnual = computedEntries.reduce((a, e) => a + e.annual_cost, 0);
      const finalCrofData = {
        ...crofData,
        reader_entries: computedEntries,
        total_annual_cost: computedTotalAnnual,
      };

      const agreementData = {
        collection_id: collectionId,
        proposal_name: proposal?.name || proposal?.collection_name || 'Agreement',
        services: (proposal?.scenarios || []).map(scenario => ({
          id: scenario.id,
          service_name: scenario.service_name,
          proposed_pricing: scenario.proposed_pricing || {},
          reader_cost_recovery: scenario.reader_cost_recovery || null,
          scenario_info: scenario.scenario_info
        })),
        end_user_name: endUserName,
        agency_name: agencyName,
        contact_name: contactName,
        contact_phone: contactPhone,
        address: address,
        notes: notes,
        term_length: termLength, // NEW: Contract term length (1, 3, or 5 years)
        function_selections: functionSelections,
        principal_name: principalName,
        principal_title: principalTitle,
        principal_date: principalDate,
        end_user_signature_name: endUserSignatureName,
        end_user_signature_title: endUserSignatureTitle,
        end_user_signature_date: endUserSignatureDate,
        use_bullet_format: useBulletFormat,
        status: 'draft',
        ach_data: { ...achData, ach_services: achData.ach_services?.length ? achData.ach_services : getAchServices(proposal?.scenarios) },
        crof_data: finalCrofData,
        impl_checklist: implChecklist,
      };

      let response;
      if (agreement) {
        // Update existing agreement
        response = await api.put(`/agreements/${agreement.id}`, agreementData);
        setAgreement({ ...agreement, ...agreementData });
      } else {
        // Create new agreement
        response = await api.post('/agreements', agreementData);
        // Set agreement with the returned ID
        setAgreement({ 
          ...agreementData, 
          id: response.data.agreement_id 
        });
      }
      
      toast.success('Agreement saved successfully');
    } catch (error) {
      console.error('Error saving agreement:', error);
      toast.error('Failed to save agreement');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    if (!agreement) {
      toast.error('Please save the agreement first');
      return;
    }

    try {
      setExporting(true);
      const response = await api.post(`/agreements/${agreement.id}/export-pdf`, {
        template: template
      }, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = (proposal?.name || proposal?.collection_name || 'Agreement').replace(/\s+/g, '_');
      link.download = `Agreement_${fileName}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const toggleFunctionSelection = (serviceId, functionCode) => {
    setFunctionSelections(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [functionCode]: !prev[serviceId]?.[functionCode]
      }
    }));
  };

  // Compute CROF reader entries from proposal scenarios (called on demand)
  const buildCrofReaderEntries = (scenarios, readerTypesMap = {}) => {
    const entries = [];
    let totalAnnual = 0;
    (scenarios || []).forEach(s => {
      const qty = parseInt(s.scenario_info?.readersQty || 0);
      const rtype = s.scenario_info?.readerType;
      if (!qty || !rtype || rtype === 'N/A') return;
      const monthlyUnit = readerTypesMap[rtype] || 0;
      const annual = monthlyUnit * qty * 12;
      totalAnnual += annual;
      entries.push({ service_name: s.service_name, reader_type: rtype, quantity: qty, monthly_unit_cost: monthlyUnit, annual_cost: annual });
    });
    return { entries, totalAnnual };
  };

  // Derive ACH services list from proposal scenarios
  const getAchServices = (scenarios) =>
    (scenarios || []).filter(s => (s.proposed_pricing?.paymentRates?.ACH?.minFee || 0) > 0)
      .map(s => ({ service_id: s.id, service_name: s.service_name, ach_fee: s.proposed_pricing?.paymentRates?.ACH?.minFee || 0, ach_limit: 'No Limit', return_handler: 'Government Window' }));

  // eCheck cell label in Schedule A preview
  const echeckLabel = (scenario) => {
    const funcs = functionSelections[scenario.id] || {};
    const fee = scenario.proposed_pricing?.paymentRates?.ACH?.minFee;
    if (!fee && fee !== 0) return '—';
    if (funcs.P && !funcs.W && !funcs.I) return 'N/A (POS)';
    if (funcs.P && (funcs.W || funcs.I)) return `$${fee.toFixed(2)} *`;
    return `$${fee?.toFixed(2) || '—'}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading agreement...</p>
        </div>
      </div>
    );
  }

  if (!proposal || !template) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Failed to load agreement data</p>
          <button onClick={() => navigate('/agreements')} className="btn-primary mt-4">
            Back to Agreements
          </button>
        </div>
      </div>
    );
  }

  // Compute visible agreement numbers dynamically
  const _achSvcs = getAchServices(proposal?.scenarios);
  const _rdrSvcs = (proposal?.scenarios || []).filter(s =>
    s.scenario_info?.readersQty > 0 && s.scenario_info?.readerType && s.scenario_info?.readerType !== 'N/A'
  );
  const _hasACH = _achSvcs.length > 0;
  const _hasCROF = _rdrSvcs.length > 0;
  let _agreementIdx = 1;
  const _saasNum = _agreementIdx;
  const _achNum = _hasACH ? ++_agreementIdx : null;
  const _crofNum = _hasCROF ? ++_agreementIdx : null;
  const _implNum = ++_agreementIdx;
  const _totalAgreements = _agreementIdx;

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="max-w-[900px] mx-auto px-6">
        {/* Header Actions */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between sticky top-0 z-10">
          <button
            onClick={() => navigate('/agreements')}
            className="flex items-center gap-2 text-slate-700 hover:text-slate-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Agreements</span>
          </button>
          <div className="flex gap-2">
            <button
              onClick={handleSaveAgreement}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Agreement'}
            </button>
            {agreement && (
              <button
                onClick={handleExportPdf}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'Download PDF'}
              </button>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* AGREEMENT 1 OF 4 — SaaS AGREEMENT                                    */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-indigo-700 text-white px-6 py-3 rounded-t-lg shadow-md flex items-center justify-between" data-testid="agreement-1-banner">
          <span className="font-bold text-sm tracking-wider">AGREEMENT {_saasNum} OF {_totalAgreements}</span>
          <span className="font-bold text-base">SaaS Agreement</span>
          <span className="text-xs text-indigo-200">Software-as-a-Service License</span>
        </div>

        {/* Agreement Document */}
        <div className="bg-white shadow-lg rounded-b-lg border-2 border-indigo-200 border-t-0 p-12 mb-12" style={{ fontFamily: 'Arial, sans-serif' }}>
          {/* Header */}
          <div className="border-b-2 border-slate-900 pb-4 mb-6 flex flex-col items-center">
            <img
              src="/letterhead.png"
              alt="Government Window"
              className="w-full max-w-2xl mb-2"
              style={{ display: 'block', margin: '0 auto' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <h1
              className="text-2xl font-bold text-indigo-900 text-center mb-2"
              style={{ display: 'none' }}
            >
              {template.header.logo}
            </h1>
            <h2 className="text-xl font-bold text-center">
              {template.header.title}
            </h2>
            {/* Customer Name */}
            {endUserName && (
              <div className="mt-3 text-center">
                <span className="text-sm font-semibold text-slate-600">Customer:</span>
                <span className="text-lg font-bold text-indigo-800 ml-2">{endUserName}</span>
              </div>
            )}
          </div>

          {/* Principal Information */}
          <div className="mb-6">
            <p className="font-semibold mb-2">Principal:</p>
            <p className="font-medium">{template.principal.name}</p>
            <p className="text-sm text-slate-600">{template.principal.address}</p>
          </div>

          {/* Grant Statement */}
          <div className="bg-slate-50 p-4 rounded border border-slate-200 mb-4">
            <p className="text-sm leading-relaxed">{template.grant.statement}</p>
          </div>

          {/* System of Record Disclaimer — REQUIRED */}
          <div className="bg-amber-50 border-2 border-amber-400 rounded p-4 mb-6" data-testid="system-of-record-disclaimer">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-900 text-sm mb-1 uppercase tracking-wide">
                  Government Window Is Not the System of Record
                </p>
                <p className="text-xs text-amber-800 leading-relaxed">
                  Government Window, LLC is a payment processing service provider and is <strong>NOT the system of record</strong> for payment calculations. All payment amounts, transaction data, and financial figures are derived from the Customer's own billing and financial systems. GW processes payments based solely on data supplied by the Customer and makes no representations or warranties regarding the accuracy, completeness, or reliability of such data. The Customer is solely responsible for ensuring the accuracy of all payment information entered into or transmitted through GW's systems.
                </p>
              </div>
            </div>
          </div>

          {/* End-User Information - EDITABLE */}
          <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-300 rounded">
            <h3 className="font-bold mb-4 text-blue-900">End-User (Licensee) Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1 flex items-center gap-1">
                  End-User (Licensee): <span className="text-red-600">*</span> <Edit2 className="w-3 h-3 text-blue-600" />
                </label>
                <input
                  type="text"
                  value={endUserName}
                  onChange={(e) => setEndUserName(e.target.value)}
                  className="input-field w-full"
                  placeholder="Enter end-user name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1 flex items-center gap-1">
                  Agency Name: <Edit2 className="w-3 h-3 text-blue-600" />
                </label>
                <input
                  type="text"
                  value={agencyName}
                  onChange={(e) => setAgencyName(e.target.value)}
                  className="input-field w-full"
                  placeholder="Enter agency name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1 flex items-center gap-1">
                  Contact Name: <Edit2 className="w-3 h-3 text-blue-600" />
                </label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="input-field w-full"
                  placeholder="Enter contact name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1 flex items-center gap-1">
                  Contact Phone: <Edit2 className="w-3 h-3 text-blue-600" />
                </label>
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  className="input-field w-full"
                  placeholder="Enter phone number"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-semibold text-slate-700 block mb-1 flex items-center gap-1">
                  Address: <Edit2 className="w-3 h-3 text-blue-600" />
                </label>
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="input-field w-full"
                  rows="2"
                  placeholder="Enter address"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1 flex items-center gap-1">
                  Contract Term Length: <span className="text-red-600">*</span> <Edit2 className="w-3 h-3 text-blue-600" />
                </label>
                <select
                  value={termLength}
                  onChange={(e) => setTermLength(Number(e.target.value))}
                  className="input-field w-full"
                >
                  <option value={1}>1 Year</option>
                  <option value={3}>3 Years</option>
                  <option value={5}>5 Years</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">Standard term length for this agreement</p>
              </div>
            </div>
          </div>

          {/* Schedule A */}
          <div className="mb-6">
            <h3 className="font-bold text-lg mb-2">Schedule "A" - Licensed Items</h3>
            <p className="text-xs text-slate-600 mb-2">{template.scheduleA.intro}</p>
            <p className="text-xs text-slate-600 mb-4 italic">
              Check the applicable function(s) per module. [IVR] = Interactive Voice Response, [WEB] = Web Pay, [POS] = Point of Sale.
              eCheck/ACH is not applicable for POS transactions.
            </p>

            <div className="border-2 border-slate-900 rounded overflow-hidden overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="px-3 py-2 text-left">Module</th>
                    <th className="px-2 py-2 text-center w-10">IVR</th>
                    <th className="px-2 py-2 text-center w-10">WEB</th>
                    <th className="px-2 py-2 text-center w-10">POS</th>
                    <th className="px-2 py-2 text-center">Reader Type</th>
                    <th className="px-2 py-2 text-center">Qty</th>
                    <th className="px-2 py-2 text-center">CC Rate</th>
                    <th className="px-2 py-2 text-center">CC Min</th>
                    <th className="px-2 py-2 text-center">AmEx Rate</th>
                    <th className="px-2 py-2 text-center">AmEx Min</th>
                    <th className="px-2 py-2 text-center">E-Check</th>
                  </tr>
                </thead>
                <tbody>
                  {proposal.scenarios?.map((scenario, idx) => {
                    const funcs = functionSelections[scenario.id] || {};
                    const pr = scenario.proposed_pricing?.paymentRates || {};
                    const posOnly = funcs.P && !funcs.W && !funcs.I;
                    return (
                      <tr key={scenario.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="border-t border-slate-300 px-3 py-2 font-medium">{scenario.service_name}</td>
                        <td className="border-t border-slate-300 px-2 py-2 text-center">
                          <input type="checkbox" checked={!!funcs.I} onChange={() => toggleFunctionSelection(scenario.id, 'I')} className="rounded w-3 h-3" />
                        </td>
                        <td className="border-t border-slate-300 px-2 py-2 text-center">
                          <input type="checkbox" checked={!!funcs.W} onChange={() => toggleFunctionSelection(scenario.id, 'W')} className="rounded w-3 h-3" />
                        </td>
                        <td className="border-t border-slate-300 px-2 py-2 text-center">
                          <input type="checkbox" checked={!!funcs.P} onChange={() => toggleFunctionSelection(scenario.id, 'P')} className="rounded w-3 h-3" />
                        </td>
                        <td className="border-t border-slate-300 px-2 py-2 text-center">{scenario.scenario_info?.readerType || '—'}</td>
                        <td className="border-t border-slate-300 px-2 py-2 text-center">{scenario.scenario_info?.readersQty || '—'}</td>
                        <td className="border-t border-slate-300 px-2 py-2 text-center">{pr.CC?.takeRate?.toFixed(2)}%</td>
                        <td className="border-t border-slate-300 px-2 py-2 text-center">${pr.CC?.minFee?.toFixed(2)}</td>
                        <td className="border-t border-slate-300 px-2 py-2 text-center">{pr.AMEX?.takeRate?.toFixed(2)}%</td>
                        <td className="border-t border-slate-300 px-2 py-2 text-center">${pr.AMEX?.minFee?.toFixed(2)}</td>
                        <td className={`border-t border-slate-300 px-2 py-2 text-center ${posOnly ? 'text-slate-400 italic' : ''}`}>
                          {echeckLabel(scenario)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {(proposal?.scenarios || []).some(s => { const f = functionSelections[s.id] || {}; return f.P && (f.W || f.I); }) && (
              <p className="text-xs text-slate-400 italic mt-1">* eCheck/ACH is not applicable for POS transactions</p>
            )}
          </div>

          {/* Stipulations & Notes - EDITABLE */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold flex items-center gap-2">
                Stipulations & Notes <Edit2 className="w-4 h-4 text-blue-600" />
              </h3>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={useBulletFormat}
                  onChange={(e) => setUseBulletFormat(e.target.checked)}
                  className="w-4 h-4 cursor-pointer"
                />
                <span className="text-slate-700">Bullet format</span>
              </label>
            </div>
            {template?.stipulations?.costAllocation && (
              <p className="text-sm mb-3">{template.stipulations.costAllocation}</p>
            )}

            {/* Show preview when bullet format is enabled */}
            {useBulletFormat && notes && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-2">
                <p className="text-xs font-semibold text-blue-800 mb-1">Preview (Bulleted Format):</p>
                <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
                  {notes.split('\n').filter(line => line.trim()).map((line, idx) => (
                    <li key={idx}>{line.trim()}</li>
                  ))}
                </ul>
              </div>
            )}

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input-field w-full"
              rows="4"
              placeholder={useBulletFormat ? "Enter each stipulation on a new line (will display as bullets)" : "Add custom notes and stipulations here..."}
            />
          </div>

          {/* Terms and Conditions (BEFORE signatures per user request) */}
          {template.termsAndConditions?.content && (
            <div className="mb-6">
              <h3 className="font-bold text-lg mb-2">Terms and Conditions</h3>
              <div className="text-sm leading-relaxed whitespace-pre-wrap bg-slate-50 p-4 rounded border border-slate-200">
                {template.termsAndConditions.content}
              </div>
            </div>
          )}

          {/* SaaS Signature Blocks (last item in SaaS section) */}
          <div className="mt-4 mb-2 flex items-center gap-3" data-testid="saas-signature-page-marker">
            <div className="flex-grow border-t-2 border-slate-400"></div>
            <span className="text-xs font-bold text-indigo-700 tracking-wider">SAAS AGREEMENT — SIGNATURE PAGE</span>
            <div className="flex-grow border-t-2 border-slate-400"></div>
          </div>
          <div className="grid grid-cols-2 gap-6 mt-4 mb-4">
            <div className="border-2 border-slate-900 p-4 rounded">
              <p className="font-bold mb-4">Principal:</p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold">Signed:</label>
                  <div className="border-b-2 border-slate-900 h-8"></div>
                </div>
                <div>
                  <label className="text-xs font-semibold flex items-center gap-1">
                    Name: <Edit2 className="w-3 h-3 text-blue-600" />
                  </label>
                  <input
                    value={principalName}
                    onChange={(e) => setPrincipalName(e.target.value)}
                    className="input-field w-full mt-1"
                    placeholder="Type principal name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold">Title:</label>
                  <input
                    value={principalTitle}
                    onChange={(e) => setPrincipalTitle(e.target.value)}
                    className="input-field w-full mt-1"
                    placeholder="Type title"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold">Date:</label>
                  <input
                    value={principalDate}
                    onChange={(e) => setPrincipalDate(e.target.value)}
                    className="input-field w-full mt-1"
                    placeholder="MM/DD/YYYY"
                  />
                </div>
              </div>
            </div>
            <div className="border-2 border-slate-900 p-4 rounded">
              <p className="font-bold mb-4">End-User:</p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold">Signed:</label>
                  <div className="border-b-2 border-slate-900 h-8"></div>
                </div>
                <div>
                  <label className="text-xs font-semibold flex items-center gap-1">
                    Name: <Edit2 className="w-3 h-3 text-blue-600" />
                  </label>
                  <input
                    value={endUserSignatureName}
                    onChange={(e) => setEndUserSignatureName(e.target.value)}
                    className="input-field w-full mt-1"
                    placeholder="Type end-user name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold">Title:</label>
                  <input
                    value={endUserSignatureTitle}
                    onChange={(e) => setEndUserSignatureTitle(e.target.value)}
                    className="input-field w-full mt-1"
                    placeholder="Type title"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold">Date:</label>
                  <input
                    value={endUserSignatureDate}
                    onChange={(e) => setEndUserSignatureDate(e.target.value)}
                    className="input-field w-full mt-1"
                    placeholder="MM/DD/YYYY"
                  />
                </div>
              </div>
            </div>
          </div>

        </div> {/* ── end of SaaS Agreement card ── */}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* AGREEMENT 2 OF 4 — ACH/eCheck PROCESSING AGREEMENT                   */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {(() => {
          const achSvcs = getAchServices(proposal?.scenarios);
          if (achSvcs.length === 0) return null;
          return (
            <>
              {/* Banner Header for ACH Agreement */}
              <div className="bg-indigo-700 text-white px-6 py-3 rounded-t-lg shadow-md flex items-center justify-between mt-8" data-testid="agreement-2-banner">
                <span className="font-bold text-sm tracking-wider">AGREEMENT {_achNum} OF {_totalAgreements}</span>
                <span className="font-bold text-base">ACH/eCheck Processing Agreement</span>
                <span className="text-xs text-indigo-200">Electronic Payment Authorization</span>
              </div>

              {/* ACH Agreement Card */}
              <div className="bg-white shadow-lg rounded-b-lg border-2 border-indigo-200 border-t-0 p-12 mb-12" style={{ fontFamily: 'Arial, sans-serif' }} data-testid="ach-agreement-section">
                {/* Logo and Customer Name Header */}
                <div className="border-b-2 border-slate-300 pb-4 mb-6 flex flex-col items-center">
                  <img
                    src="/letterhead.png"
                    alt="Government Window"
                    className="w-full max-w-md mb-2"
                    style={{ display: 'block', margin: '0 auto' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <h3 className="font-bold text-lg text-indigo-800">ACH / eCheck Processing Agreement</h3>
                  {endUserName && (
                    <div className="mt-2 text-center">
                      <span className="text-sm font-semibold text-slate-600">Customer:</span>
                      <span className="text-base font-bold text-indigo-800 ml-2">{endUserName}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-4">
                  This section applies because one or more services include eCheck/ACH pricing.
                  Module names and ACH fees are locked (auto-filled from pricing).
                </p>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">EIN</label>
                    <input value={achData.ein || ''} onChange={e => setAchData(d => ({ ...d, ein: e.target.value }))}
                      className="border-b border-slate-400 w-full px-1 py-0.5 focus:outline-none" placeholder="XX-XXXXXXX" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Website</label>
                    <input value={achData.website || ''} onChange={e => setAchData(d => ({ ...d, website: e.target.value }))}
                      className="border-b border-slate-400 w-full px-1 py-0.5 focus:outline-none" placeholder="https://..." />
                  </div>
                </div>

                <div className="border-2 border-slate-900 rounded overflow-hidden mb-4">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-900 text-white">
                      <tr>
                        <th className="px-3 py-2 text-left">Module</th>
                        <th className="px-3 py-2 text-center">ACH Limit</th>
                        <th className="px-3 py-2 text-center">ACH Fee (per txn)</th>
                        <th className="px-3 py-2 text-center">Return Handler</th>
                        <th className="px-3 py-2 text-center">Bank Acct Last 4</th>
                      </tr>
                    </thead>
                    <tbody>
                      {achSvcs.map((svc, idx) => {
                        const saved = (achData.ach_services || []).find(s => s.service_id === svc.service_id || s.service_name === svc.service_name) || {};
                        return (
                          <tr key={svc.service_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border-t border-slate-300 px-3 py-2 font-medium text-slate-600">{svc.service_name}</td>
                            <td className="border-t border-slate-300 px-3 py-2 text-center">
                              <input value={saved.ach_limit || 'No Limit'}
                                onChange={e => setAchData(d => ({ ...d, ach_services: achSvcs.map(s => s.service_id === svc.service_id ? { ...svc, ...saved, ach_limit: e.target.value } : ((d.ach_services || []).find(x => x.service_id === s.service_id) || s)) }))}
                                className="border-b border-slate-300 text-center w-full focus:outline-none text-xs" />
                            </td>
                            <td className="border-t border-slate-300 px-3 py-2 text-center text-slate-500 font-medium">${svc.ach_fee.toFixed(2)}</td>
                            <td className="border-t border-slate-300 px-3 py-2 text-center">
                              <input value={saved.return_handler || 'Government Window'}
                                onChange={e => setAchData(d => ({ ...d, ach_services: achSvcs.map(s => s.service_id === svc.service_id ? { ...svc, ...saved, return_handler: e.target.value } : ((d.ach_services || []).find(x => x.service_id === s.service_id) || s)) }))}
                                className="border-b border-slate-300 text-center w-full focus:outline-none text-xs" />
                            </td>
                            <td className="border-t border-slate-300 px-3 py-2 text-center">
                              <input
                                value={saved.bank_account_last4 || ''}
                                onChange={e => setAchData(d => ({ ...d, ach_services: achSvcs.map(s => s.service_id === svc.service_id ? { ...svc, ...saved, bank_account_last4: e.target.value.replace(/\D/g, '').slice(0, 4) } : ((d.ach_services || []).find(x => x.service_id === s.service_id) || s)) }))}
                                className="border-b border-slate-300 text-center w-full focus:outline-none text-xs font-mono"
                                placeholder="XXXX"
                                maxLength={4}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* AUTHORIZATION (verbatim) */}
                <div className="bg-slate-50 border border-slate-200 rounded p-4 mb-4">
                  <h4 className="font-bold text-sm text-slate-800 mb-2">AUTHORIZATION</h4>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    {template?.achAgreement?.authorizationText ||
                      'Licensee hereby authorizes the Principal to initiate Automated Clearing House (ACH) credit and or debit entries to/from the Checking Account/Savings Account indicated by the Voided Check or Bank Letter provided to the Principal and its Receivers\' accounts. Licensee acknowledges that the origination of ACH transactions to the bank account must comply with the provisions of U.S. law and NACHA Rules. The Originator also agrees to be bound to NACHA Rules and agree not to violate U.S. law. Furthermore, all ACH credits and or debits must be for services provided or rendered in accordance with the agreements between the Principal and the Licensee. ACH transactions will also be limited to PPD (Pre-Arranged Payment), TEL (Telephone Initiated Payment), and WEB (Internet Initiated Payment), with a history of the ACH transactions retained on the Principal\'s system per the NACHA Rules. This authorization is to remain in full force and effect until the Principal has received notification from the Licensee in writing to terminate this authorization. Licensee agrees to give such notice 30 days prior to the next scheduled draft to allow the Principal a reasonable opportunity to act on it. Principal or its ODFI has the right to audit the Licensee for compliance with the NACHA Rules and may terminate this agreement for breach of the NACHA Rules. This service may not be shared.'}
                  </p>
                </div>

                {/* TERMS AND CONDITIONS (verbatim) */}
                <div className="bg-slate-50 border border-slate-200 rounded p-4 mb-4">
                  <h4 className="font-bold text-sm text-slate-800 mb-2">TERMS AND CONDITIONS</h4>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    {template?.achAgreement?.termsAndConditionsText ||
                      <>Licensee certifies that all information set forth in this completed ACH/Check Processing Agreement is true and correct and that Licensee has received a copy of the ACH/Check Processing Terms and Conditions from the Principal which along with this Addendum comprise the Agreement (see <a href="http://www.governmentwindow.com" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">http://www.governmentwindow.com</a>).</>
                    }
                  </p>
                  <p className="text-xs text-slate-700 mt-3 italic">
                    The undersigned have executed this Agreement as of the date(s) indicated below.
                  </p>
                </div>

                {/* ── ACH SIGNATURE PAGE (visible drawn lines) ── */}
                <div className="mt-4 mb-2 flex items-center gap-3" data-testid="ach-signature-page-marker">
                  <div className="flex-grow border-t-2 border-slate-400"></div>
                  <span className="text-xs font-bold text-indigo-700 tracking-wider">ACH AGREEMENT — SIGNATURE PAGE</span>
                  <div className="flex-grow border-t-2 border-slate-400"></div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="border-2 border-slate-900 p-4 rounded" data-testid="ach-signature-principal">
                    <p className="font-bold text-sm mb-3">PRINCIPAL — Government Window, LLC</p>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-600">Signed</label>
                        <div className="border-b-2 border-slate-900 h-8"></div>
                      </div>
                      {[['principal_name','Name'],['principal_title','Title'],['principal_date','Date']].map(([k,lbl]) => (
                        <div key={k}>
                          <label className="text-xs font-semibold text-slate-600">{lbl}</label>
                          <input value={achData[k] || ''} onChange={e => setAchData(d => ({ ...d, [k]: e.target.value }))}
                            className="border-b-2 border-slate-900 w-full px-2 py-1 text-sm focus:outline-none focus:border-indigo-600"
                            placeholder={`Type ${lbl.toLowerCase()} here`} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="border-2 border-slate-900 p-4 rounded" data-testid="ach-signature-end-user">
                    <p className="font-bold text-sm mb-3">END-USER / LICENSEE</p>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-600">Signed</label>
                        <div className="border-b-2 border-slate-900 h-8"></div>
                      </div>
                      {[['client_name','Name'],['client_title','Title'],['client_date','Date']].map(([k,lbl]) => (
                        <div key={k}>
                          <label className="text-xs font-semibold text-slate-600">{lbl}</label>
                          <input value={achData[k] || ''} onChange={e => setAchData(d => ({ ...d, [k]: e.target.value }))}
                            className="border-b-2 border-slate-900 w-full px-2 py-1 text-sm focus:outline-none focus:border-indigo-600"
                            placeholder={`Type ${lbl.toLowerCase()} here`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* AGREEMENT 3 OF 4 — CARD READER ORDER FORM                           */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {(() => {
          const rdrSvcs = (proposal?.scenarios || []).filter(s => s.scenario_info?.readersQty > 0 && s.scenario_info?.readerType && s.scenario_info?.readerType !== 'N/A');
          if (rdrSvcs.length === 0) return null;
            // Build entries from scenarios; pull monthly_unit_cost from settings reader-types
            // Saved entries take precedence; otherwise compute from settings
            const savedByKey = {};
            (crofData.reader_entries || []).forEach(e => {
              savedByKey[`${e.service_name}|${e.reader_type}`] = e;
            });
            const entries = rdrSvcs.map(s => {
              const rtype = s.scenario_info?.readerType;
              const qty = s.scenario_info?.readersQty || 0;
              const saved = savedByKey[`${s.service_name}|${rtype}`];
              const settings = readerTypesMap[rtype] || {};
              // Settings is the source of truth; saved value used only if settings lookup fails
              const monthly = settings.monthly_cost != null
                ? settings.monthly_cost
                : (saved?.monthly_unit_cost || 0);
              return {
                service_name: s.service_name,
                reader_type: rtype,
                quantity: qty,
                monthly_unit_cost: monthly,
                annual_cost: monthly * qty * 12,
              };
            });
            const totalAnnual = entries.reduce((a, e) => a + (e.monthly_unit_cost * e.quantity * 12), 0);
            const ratePct = crofData.rate_increase_pct || 0;
            return (
              <>
                {/* Banner Header for CROF Agreement */}
                <div className="bg-green-700 text-white px-6 py-3 rounded-t-lg shadow-md flex items-center justify-between mt-8" data-testid="agreement-3-banner">
                  <span className="font-bold text-sm tracking-wider">AGREEMENT {_crofNum} OF {_totalAgreements}</span>
                  <span className="font-bold text-base">Card Reader Order Form</span>
                  <span className="text-xs text-green-200">Equipment Order & Terms of Supply</span>
                </div>

                {/* CROF Agreement Card */}
                <div className="bg-white shadow-lg rounded-b-lg border-2 border-green-200 border-t-0 p-12 mb-12" style={{ fontFamily: 'Arial, sans-serif' }}>
                  {/* Logo and Customer Name Header */}
                  <div className="border-b-2 border-slate-300 pb-4 mb-6 flex flex-col items-center">
                    <img
                      src="/letterhead.png"
                      alt="Government Window"
                      className="w-full max-w-md mb-2"
                      style={{ display: 'block', margin: '0 auto' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <h3 className="font-bold text-lg text-green-800">Card Reader Order Form</h3>
                    {endUserName && (
                      <div className="mt-2 text-center">
                        <span className="text-sm font-semibold text-slate-600">Customer:</span>
                        <span className="text-base font-bold text-green-800 ml-2">{endUserName}</span>
                      </div>
                    )}
                  </div>
                <p className="text-xs text-slate-500 mb-4">Select one of the three cost options below. Unit prices and totals are locked (auto-calculated from pricing).</p>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  {[['sales_person','Sales Person'],['requestor','Requestor'],['shipping_address','Shipping Address'],['contact_phone','Contact Phone'],['contact_email','Contact Email']].map(([k,lbl]) => (
                    <div key={k}>
                      <label className="text-xs font-semibold text-slate-600">{lbl}</label>
                      <input value={crofData[k] || ''} onChange={e => setCrofData(d => ({ ...d, [k]: e.target.value }))}
                        className="border-b border-slate-400 w-full px-1 py-0.5 focus:outline-none text-sm" />
                    </div>
                  ))}
                  <div className="col-span-2">
                    <label className="text-xs font-semibold text-slate-600">Additional Notes</label>
                    <input value={crofData.notes || ''} onChange={e => setCrofData(d => ({ ...d, notes: e.target.value }))}
                      className="border-b border-slate-400 w-full px-1 py-0.5 focus:outline-none text-sm" />
                  </div>
                </div>

                {/* Reader Table */}
                <div className="border-2 border-slate-900 rounded overflow-hidden mb-4">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-900 text-white">
                      <tr>
                        <th className="px-3 py-2 text-left">Service</th>
                        <th className="px-3 py-2 text-center">Reader Type</th>
                        <th className="px-3 py-2 text-center">Qty</th>
                        <th className="px-3 py-2 text-center">Unit Price ($/mo)</th>
                        <th className="px-3 py-2 text-center">Annual Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e, idx) => {
                        const missingCost = (e.monthly_unit_cost || 0) === 0 && e.reader_type && e.reader_type !== 'N/A';
                        return (
                          <tr key={idx} className={missingCost ? 'bg-amber-50' : (idx % 2 === 0 ? 'bg-white' : 'bg-slate-50')}>
                            <td className="border-t border-slate-300 px-3 py-2">{e.service_name}</td>
                            <td className="border-t border-slate-300 px-3 py-2 text-center">{e.reader_type}</td>
                            <td className="border-t border-slate-300 px-3 py-2 text-center">{e.quantity}</td>
                            <td className="border-t border-slate-300 px-3 py-2 text-center text-slate-500">
                              {missingCost ? (
                                <span className="text-amber-700" title={`'${e.reader_type}' not configured in Settings → Reader Types`}>
                                  ⚠ not configured
                                </span>
                              ) : (
                                <>${(e.monthly_unit_cost || 0).toFixed(2)}/mo</>
                              )}
                            </td>
                            <td className="border-t border-slate-300 px-3 py-2 text-center text-slate-500">${((e.monthly_unit_cost || 0) * e.quantity * 12).toFixed(2)}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-slate-100 font-bold">
                        <td colSpan={4} className="border-t border-slate-300 px-3 py-2 text-right">Total Annual</td>
                        <td className="border-t border-slate-300 px-3 py-2 text-center">${totalAnnual.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* 3 Option Boxes */}
                <p className="font-semibold text-sm text-slate-700 mb-2">Card Reader Cost Option Selection:</p>
                <div className="space-y-2" role="radiogroup" aria-label="Card Reader Cost Option Selection">
                  {[
                    { opt: 1, title: 'Option 1: Pass-Through (Client Assumes Reader Costs)', desc: `Client assumes $${totalAnnual.toFixed(2)}/year in card reader costs.` },
                    { opt: 2, title: 'Option 2: Cost Recovery (Rate Adjustment)', desc: `Rate adjustment of +${ratePct.toFixed(3)}% per transaction recovers $${totalAnnual.toFixed(2)} annually.` },
                    { opt: 3, title: 'Option 3: GW Absorbs Reader Costs', desc: `Government Window absorbs $${totalAnnual.toFixed(2)}/year as our value proposition — a direct cost savings to the Licensee.` },
                  ].map(({ opt, title, desc }) => (
                    <label key={opt} data-testid={`crof-cost-option-${opt}`} className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${crofData.selected_option === opt ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="crof-cost-option"
                        value={opt}
                        checked={crofData.selected_option === opt}
                        onChange={() => setCrofData(d => ({ ...d, selected_option: opt }))}
                        className="mt-0.5 w-4 h-4 text-indigo-600"
                      />
                      <div>
                        <p className="font-semibold text-sm">{title}</p>
                        <p className="text-xs text-slate-600">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Delivery fields */}
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {[['serial_numbers','Serial Numbers'],['tracking_number','Tracking Number'],['date_of_delivery','Date of Delivery']].map(([k,lbl]) => (
                    <div key={k}>
                      <label className="text-xs font-semibold text-slate-600">{lbl}</label>
                      <input value={crofData[k] || ''} onChange={e => setCrofData(d => ({ ...d, [k]: e.target.value }))}
                        className="border-b border-slate-400 w-full px-1 py-0.5 focus:outline-none text-sm" />
                    </div>
                  ))}
                </div>

                {/* TERMS OF SUPPLY (verbatim) */}
                <div className="bg-slate-50 border border-slate-200 rounded p-4 mt-6 mb-4">
                  <h4 className="font-bold text-sm text-slate-800 mb-2">TERMS OF SUPPLY</h4>
                  {(template?.crofAgreement?.termsOfSupply || [
                    { heading: '1. Equipment Ownership and Responsibility', body: 'This Terms of Supply ("Terms") applies to the card reader(s) as depicted on the front of this order ("Equipment") provided by Government Window ("GW") to you, the customer whose name appears on the front hereof ("Customer"). The Equipment remains the property of GW at all times.' },
                    { heading: '2. Use of Equipment', body: 'The Equipment is provided solely for use with GW\'s credit card processing services. The customer agrees to use the Equipment in accordance with all applicable laws, regulations, and instructions provided by GW. The customer is responsible for the safekeeping and proper use of the Equipment.' },
                    { heading: '3. Return of Equipment', body: 'The customer agrees to return the Equipment to GW upon termination of the credit card processing agreement, regardless of the reason for termination. The Equipment must be returned in good working order and with all accessories (if applicable).' },
                    { heading: '4. Failure to Return Equipment', body: 'In the event that Customer fails to return the Equipment within thirty (30) business days of termination, Customer will be charged a pro-rated portion of the replacement cost of each missing unit. The replacement cost will be determined by GW in its sole discretion but shall not exceed the value as new.' },
                    { heading: '5. Customer Acknowledgement', body: 'By signing below, Customer acknowledges that they have read and understood these Terms and agree to the following:' },
                  ]).map((sec, i) => (
                    <div key={i} className="mb-2">
                      <p className="text-xs font-semibold text-slate-800">{sec.heading}</p>
                      <p className="text-xs text-slate-700 leading-relaxed">{sec.body}</p>
                    </div>
                  ))}
                  <ul className="list-disc pl-6 text-xs text-slate-700 mt-2 space-y-1">
                    {(template?.crofAgreement?.acknowledgementBullets || [
                      'The Equipment remains the property of GW.',
                      'The customer is responsible for the safekeeping and proper use of the Equipment.',
                      'The customer agrees to return the Equipment to GW upon termination of the credit card processing agreement.',
                      'The customer agrees to be charged a pro-rated portion of the replacement cost for any missing Equipment.',
                    ]).map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>

                {/* ── CROF SIGNATURE PAGE (visible drawn lines) ── */}
                <div className="mt-4 mb-2 flex items-center gap-3" data-testid="crof-signature-page-marker">
                  <div className="flex-grow border-t-2 border-slate-400"></div>
                  <span className="text-xs font-bold text-green-800 tracking-wider">CARD READER ORDER FORM — SIGNATURE PAGE</span>
                  <div className="flex-grow border-t-2 border-slate-400"></div>
                </div>
                <div className="border-2 border-slate-900 p-4 rounded" data-testid="crof-signature-customer">
                  <p className="font-bold text-sm mb-3">CUSTOMER SIGNATURE</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600">Signed</label>
                      <div className="border-b-2 border-slate-900 h-8"></div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600">Print Name</label>
                      <input
                        value={crofData.customer_signature_name || ''}
                        onChange={e => setCrofData(d => ({ ...d, customer_signature_name: e.target.value }))}
                        className="border-b-2 border-slate-900 w-full px-2 py-1 text-sm focus:outline-none focus:border-green-700"
                        placeholder="Type customer name here" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600">Date</label>
                      <input
                        value={crofData.customer_signature_date || ''}
                        onChange={e => setCrofData(d => ({ ...d, customer_signature_date: e.target.value }))}
                        className="border-b-2 border-slate-900 w-full px-2 py-1 text-sm focus:outline-none focus:border-green-700"
                        placeholder="MM/DD/YYYY" />
                    </div>
                  </div>
                </div>

                {/* End of CROF marker - updated to 3 of 4 */}
                <div className="mt-6 border-t-4 border-double border-slate-900 pt-3" data-testid="end-of-crof-marker">
                  <div className="bg-slate-100 px-4 py-3 rounded text-center">
                    <span className="text-xs font-bold text-slate-700 tracking-wider">END OF AGREEMENT {_crofNum} OF {_totalAgreements} — CARD READER ORDER FORM</span>
                  </div>
                </div>
                </div> {/* ── end of CROF Agreement card ── */}
              </>
            );
          })()}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* AGREEMENT 4 OF 4 — IMPLEMENTATION CHECKLIST                         */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-teal-700 text-white px-6 py-3 rounded-t-lg shadow-md flex items-center justify-between mt-8" data-testid="agreement-4-banner">
          <span className="font-bold text-sm tracking-wider">AGREEMENT {_implNum} OF {_totalAgreements}</span>
          <span className="font-bold text-base">Implementation Checklist</span>
          <span className="text-xs text-teal-200">Internal Handoff Document</span>
        </div>

        {/* Implementation Checklist Card */}
        <div className="bg-white shadow-lg rounded-b-lg border-2 border-teal-200 border-t-0 p-12 mb-12" style={{ fontFamily: 'Arial, sans-serif' }} data-testid="impl-checklist-section">
          {/* Logo and Customer Name Header */}
          <div className="border-b-2 border-slate-300 pb-4 mb-6 flex flex-col items-center">
            <img
              src="/letterhead.png"
              alt="Government Window"
              className="w-full max-w-md mb-2"
              style={{ display: 'block', margin: '0 auto' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
              <h3 className="font-bold text-lg text-teal-800">Implementation Checklist</h3>
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded font-medium">Internal Use Only</span>
            </div>
            {endUserName && (
              <div className="mt-2 text-center">
                <span className="text-sm font-semibold text-slate-600">Customer:</span>
                <span className="text-base font-bold text-teal-800 ml-2">{endUserName}</span>
              </div>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-6 bg-amber-50 border border-amber-200 rounded p-3">
            <strong className="text-amber-800">Important:</strong> This checklist must be completed before implementation begins. 
            Fields marked with <span className="text-red-600">*</span> are required. 
            Auto-populated fields are pulled from the proposal data.
          </p>

          {/* ══════════════════════════════════════════════════════════════ */}
          {/* PHASE 2: ENHANCED SALES-TO-IMPLEMENTATION HANDOFF SECTIONS    */}
          {/* ══════════════════════════════════════════════════════════════ */}
          
          <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6">
            <h3 className="font-bold text-lg text-blue-900 mb-4 flex items-center gap-2">
              📋 Sales-to-Implementation Handoff
              <span className="text-xs font-normal bg-blue-100 text-blue-700 px-2 py-1 rounded">Critical Information</span>
            </h3>
            <p className="text-sm text-blue-800 mb-6">
              Complete these sections during the sales process to ensure smooth implementation. 
              This information helps the implementation team understand customer needs, scope, and timeline.
            </p>

            {/* Section A: Discovery & Requirements */}
            <div className="mb-6 bg-white rounded-lg p-5 border border-blue-200">
              <h4 className="font-semibold text-sm text-blue-900 mb-4 pb-2 border-b border-blue-200 flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">A</span>
                Discovery & Requirements Capture
              </h4>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="flex items-center gap-2 cursor-pointer mb-2">
                    <input
                      type="checkbox"
                      checked={implChecklist.discovery_completed === true}
                      onChange={(e) => setImplChecklist(d => ({ ...d, discovery_completed: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm font-semibold text-slate-700">Discovery Completed</span>
                  </label>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Discovery Date</label>
                  <input
                    type="date"
                    value={implChecklist.discovery_date || ''}
                    onChange={(e) => setImplChecklist(d => ({ ...d, discovery_date: e.target.value }))}
                    className="input-field w-full"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Conducted By (Sales Rep)</label>
                <input
                  type="text"
                  value={implChecklist.discovery_conducted_by || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, discovery_conducted_by: e.target.value }))}
                  className="input-field w-full"
                  placeholder="Name of sales representative who conducted discovery"
                />
              </div>

              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Customer Pain Points</label>
                <textarea
                  value={implChecklist.customer_pain_points || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, customer_pain_points: e.target.value }))}
                  className="input-field w-full"
                  rows="2"
                  placeholder="What problems is the customer trying to solve? What challenges are they facing?"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Expected Transaction Volume</label>
                  <input
                    type="text"
                    value={implChecklist.expected_transaction_volume || ''}
                    onChange={(e) => setImplChecklist(d => ({ ...d, expected_transaction_volume: e.target.value }))}
                    className="input-field w-full"
                    placeholder="e.g., 500-1000 transactions/month"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Peak Season Information</label>
                  <input
                    type="text"
                    value={implChecklist.peak_season_info || ''}
                    onChange={(e) => setImplChecklist(d => ({ ...d, peak_season_info: e.target.value }))}
                    className="input-field w-full"
                    placeholder="e.g., Tax season (Jan-Apr), Holiday season"
                  />
                </div>
              </div>
            </div>

            {/* Section B: Scope Definition */}
            <div className="mb-6 bg-white rounded-lg p-5 border border-blue-200">
              <h4 className="font-semibold text-sm text-blue-900 mb-4 pb-2 border-b border-blue-200 flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">B</span>
                Scope Definition
                <span className="text-red-600 text-xs">* Critical</span>
              </h4>
              
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={implChecklist.scope_defined === true}
                    onChange={(e) => setImplChecklist(d => ({ ...d, scope_defined: e.target.checked }))}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <span className="text-sm font-semibold text-slate-700">Scope Clearly Defined</span>
                  {!implChecklist.scope_defined && (
                    <span className="text-xs text-red-600">⚠ Define scope to prevent disputes</span>
                  )}
                </label>
              </div>

              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-600 block mb-1">In-Scope Services <span className="text-red-600">*</span></label>
                <textarea
                  value={implChecklist.in_scope_services || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, in_scope_services: e.target.value }))}
                  className="input-field w-full border-2 border-green-200"
                  rows="2"
                  placeholder="What services and features ARE included? Be specific."
                />
              </div>

              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Out-of-Scope Items</label>
                <textarea
                  value={implChecklist.out_of_scope_items || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, out_of_scope_items: e.target.value }))}
                  className="input-field w-full border-2 border-red-200"
                  rows="2"
                  placeholder="What is explicitly NOT included? List exclusions to prevent misunderstandings."
                />
              </div>

              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Custom Requirements</label>
                <textarea
                  value={implChecklist.custom_requirements || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, custom_requirements: e.target.value }))}
                  className="input-field w-full"
                  rows="2"
                  placeholder="Any special customizations, non-standard features, or unique needs?"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Third-Party Integrations</label>
                <input
                  type="text"
                  value={implChecklist.third_party_integrations || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, third_party_integrations: e.target.value }))}
                  className="input-field w-full"
                  placeholder="e.g., Tyler Munis, CivicPlus, Salesforce"
                />
              </div>
            </div>

            {/* Section C: Data Requirements */}
            <div className="mb-6 bg-white rounded-lg p-5 border border-green-200">
              <h4 className="font-semibold text-sm text-green-900 mb-4 pb-2 border-b border-green-200 flex items-center gap-2">
                <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">C</span>
                Data Requirements
              </h4>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={implChecklist.data_migration_needed === true}
                    onChange={(e) => setImplChecklist(d => ({ ...d, data_migration_needed: e.target.checked }))}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <span className="text-sm font-semibold text-slate-700">Data Migration Needed</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={implChecklist.historical_data_required === true}
                    onChange={(e) => setImplChecklist(d => ({ ...d, historical_data_required: e.target.checked }))}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <span className="text-sm font-semibold text-slate-700">Historical Data Required</span>
                </label>
              </div>

              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Data Sources</label>
                <input
                  type="text"
                  value={implChecklist.data_sources || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, data_sources: e.target.value }))}
                  className="input-field w-full"
                  placeholder="Where is customer data coming from? (e.g., Excel, legacy system, manual entry)"
                />
              </div>

              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Data Format</label>
                <input
                  type="text"
                  value={implChecklist.data_format || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, data_format: e.target.value }))}
                  className="input-field w-full"
                  placeholder="e.g., CSV, Excel, API, Database export"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Data Cleanup Needed</label>
                <textarea
                  value={implChecklist.data_cleanup_needed || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, data_cleanup_needed: e.target.value }))}
                  className="input-field w-full"
                  rows="2"
                  placeholder="Any data quality issues, duplicates, missing fields, or standardization needed?"
                />
              </div>
            </div>

            {/* Section D: Stakeholder Contacts */}
            <div className="mb-6 bg-white rounded-lg p-5 border border-amber-200">
              <h4 className="font-semibold text-sm text-amber-900 mb-4 pb-2 border-b border-amber-200 flex items-center gap-2">
                <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">D</span>
                Stakeholder Contacts
                <span className="text-xs text-slate-500">(Key decision makers and users)</span>
              </h4>
              
              <div className="space-y-4">
                <div className="bg-amber-50 rounded p-3">
                  <p className="text-xs font-semibold text-amber-900 mb-2">Project Sponsor (Executive Level)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={implChecklist.project_sponsor || ''}
                      onChange={(e) => setImplChecklist(d => ({ ...d, project_sponsor: e.target.value }))}
                      className="input-field w-full"
                      placeholder="Name"
                    />
                    <input
                      type="email"
                      value={implChecklist.project_sponsor_email || ''}
                      onChange={(e) => setImplChecklist(d => ({ ...d, project_sponsor_email: e.target.value }))}
                      className="input-field w-full"
                      placeholder="Email"
                    />
                  </div>
                </div>

                <div className="bg-amber-50 rounded p-3">
                  <p className="text-xs font-semibold text-amber-900 mb-2">Primary User (Day-to-Day)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={implChecklist.primary_user || ''}
                      onChange={(e) => setImplChecklist(d => ({ ...d, primary_user: e.target.value }))}
                      className="input-field w-full"
                      placeholder="Name"
                    />
                    <input
                      type="email"
                      value={implChecklist.primary_user_email || ''}
                      onChange={(e) => setImplChecklist(d => ({ ...d, primary_user_email: e.target.value }))}
                      className="input-field w-full"
                      placeholder="Email"
                    />
                  </div>
                </div>

                <div className="bg-amber-50 rounded p-3">
                  <p className="text-xs font-semibold text-amber-900 mb-2">Finance Contact (Billing/Payment Questions)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={implChecklist.finance_contact || ''}
                      onChange={(e) => setImplChecklist(d => ({ ...d, finance_contact: e.target.value }))}
                      className="input-field w-full"
                      placeholder="Name"
                    />
                    <input
                      type="email"
                      value={implChecklist.finance_contact_email || ''}
                      onChange={(e) => setImplChecklist(d => ({ ...d, finance_contact_email: e.target.value }))}
                      className="input-field w-full"
                      placeholder="Email"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section E: Timeline & Milestones */}
            <div className="mb-4 bg-white rounded-lg p-5 border border-purple-200">
              <h4 className="font-semibold text-sm text-purple-900 mb-4 pb-2 border-b border-purple-200 flex items-center gap-2">
                <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">E</span>
                Timeline & Implementation Phase
              </h4>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Target Go-Live Date</label>
                  <input
                    type="date"
                    value={implChecklist.target_go_live || ''}
                    onChange={(e) => setImplChecklist(d => ({ ...d, target_go_live: e.target.value }))}
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer mt-6">
                    <input
                      type="checkbox"
                      checked={implChecklist.hard_deadline === true}
                      onChange={(e) => setImplChecklist(d => ({ ...d, hard_deadline: e.target.checked }))}
                      className="w-4 h-4 text-red-600 rounded"
                    />
                    <span className="text-sm font-semibold text-slate-700">🔴 Hard Deadline (Not Flexible)</span>
                  </label>
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Deadline Reason</label>
                <input
                  type="text"
                  value={implChecklist.deadline_reason || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, deadline_reason: e.target.value }))}
                  className="input-field w-full"
                  placeholder="Why is this date important? (e.g., Fiscal year start, budget deadline, event)"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Implementation Phase</label>
                <select
                  value={implChecklist.implementation_phase || 'not_started'}
                  onChange={(e) => setImplChecklist(d => ({ ...d, implementation_phase: e.target.value }))}
                  className="input-field w-full"
                >
                  <option value="not_started">⚪ Not Started</option>
                  <option value="discovery">🔵 Discovery</option>
                  <option value="setup">🟡 Setup / Configuration</option>
                  <option value="testing">🟠 Testing / UAT</option>
                  <option value="live">🟢 Live / In Production</option>
                </select>
              </div>
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════ */}
          {/* END ENHANCED HANDOFF SECTIONS                                  */}
          {/* ══════════════════════════════════════════════════════════════ */}

          {/* Section 1: Customer Information (Auto-populated) */}
          {isSectionVisible('customerInfo') && (
          <div className="mb-8" data-testid="impl-section-1">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
              Customer Information
              <span className="text-xs font-normal text-slate-400">(Auto-populated from proposal)</span>
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-slate-50 rounded p-3">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Customer Name</label>
                <p className="font-medium text-slate-800">{endUserName || '—'}</p>
              </div>
              <div className="bg-slate-50 rounded p-3">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Agency Name</label>
                <p className="font-medium text-slate-800">{agencyName || '—'}</p>
              </div>
              <div className="bg-slate-50 rounded p-3">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Contact Name</label>
                <p className="font-medium text-slate-800">{contactName || '—'}</p>
              </div>
              <div className="bg-slate-50 rounded p-3">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Contact Phone</label>
                <p className="font-medium text-slate-800">{contactPhone || '—'}</p>
              </div>
              <div className="col-span-2 bg-slate-50 rounded p-3">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Physical Address</label>
                <p className="font-medium text-slate-800">{address || '—'}</p>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs font-semibold text-slate-600 block mb-1">Customer Title</label>
              <input
                type="text"
                value={implChecklist.customer_title}
                onChange={(e) => setImplChecklist(d => ({ ...d, customer_title: e.target.value }))}
                className="input-field w-full"
                placeholder="e.g., County Tax Commissioner, City Clerk"
              />
            </div>
          </div>
          )}

          {/* Section 2: Go-Live Date */}
          {isSectionVisible('goLiveDate') && (
          <div className="mb-8" data-testid="impl-section-2">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
              Proposed Go-Live Date <span className="text-red-600">*</span>
            </h4>
            <div className="space-y-3">
              <div className="flex gap-4">
                {[
                  { value: 'specific', label: 'Specific Date' },
                  { value: 'range', label: 'Date Range' },
                  { value: 'na', label: 'N/A (No strict deadline)' }
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="go_live_type"
                      value={opt.value}
                      checked={implChecklist.go_live_type === opt.value}
                      onChange={(e) => setImplChecklist(d => ({ ...d, go_live_type: e.target.value }))}
                      className="w-4 h-4 text-teal-600"
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
              {implChecklist.go_live_type === 'specific' && (
                <input
                  type="date"
                  value={implChecklist.go_live_date}
                  onChange={(e) => setImplChecklist(d => ({ ...d, go_live_date: e.target.value }))}
                  className="input-field w-64"
                />
              )}
              {implChecklist.go_live_type === 'range' && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={implChecklist.go_live_range_start}
                    onChange={(e) => setImplChecklist(d => ({ ...d, go_live_range_start: e.target.value }))}
                    className="input-field w-48"
                  />
                  <span className="text-slate-500">to</span>
                  <input
                    type="date"
                    value={implChecklist.go_live_range_end}
                    onChange={(e) => setImplChecklist(d => ({ ...d, go_live_range_end: e.target.value }))}
                    className="input-field w-48"
                  />
                </div>
              )}
              {implChecklist.go_live_type === 'na' && (
                <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded">
                  ⚠️ Customers without a strict go-live date will likely be lower priority in the implementation queue.
                </p>
              )}
            </div>
          </div>
          )}

          {/* Section 3: Services Requested (Auto-populated) */}
          {isSectionVisible('servicesRates') && (
          <div className="mb-8" data-testid="impl-section-3">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">3</span>
              Services & Proposed Rates
              <span className="text-xs font-normal text-slate-400">(Auto-populated from Schedule A)</span>
            </h4>
            <div className="border-2 border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-2 py-2 text-left font-semibold">Service Name</th>
                    <th className="px-2 py-2 text-center font-semibold">IVR</th>
                    <th className="px-2 py-2 text-center font-semibold">WEB</th>
                    <th className="px-2 py-2 text-center font-semibold">POS</th>
                    <th className="px-2 py-2 text-center font-semibold bg-blue-50">CC Rate</th>
                    <th className="px-2 py-2 text-center font-semibold bg-blue-50">CC Min</th>
                    <th className="px-2 py-2 text-center font-semibold bg-purple-50">AmEx Rate</th>
                    <th className="px-2 py-2 text-center font-semibold bg-purple-50">AmEx Min</th>
                    <th className="px-2 py-2 text-center font-semibold bg-green-50">eCheck</th>
                  </tr>
                </thead>
                <tbody>
                  {proposal?.scenarios?.map((scenario, idx) => {
                    const funcs = functionSelections[scenario.id] || {};
                    const pr = scenario.proposed_pricing?.paymentRates || {};
                    const hasAmex = pr.AMEX && (pr.AMEX.takeRate > 0 || pr.AMEX.minFee > 0);
                    return (
                      <tr key={scenario.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="border-t border-slate-200 px-2 py-2 font-medium">{scenario.service_name}</td>
                        <td className="border-t border-slate-200 px-2 py-2 text-center">
                          {funcs.I ? <span className="text-green-600 font-bold">✓</span> : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="border-t border-slate-200 px-2 py-2 text-center">
                          {funcs.W ? <span className="text-green-600 font-bold">✓</span> : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="border-t border-slate-200 px-2 py-2 text-center">
                          {funcs.P ? <span className="text-green-600 font-bold">✓</span> : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="border-t border-slate-200 px-2 py-2 text-center bg-blue-50/30">{pr.CC?.takeRate?.toFixed(2)}%</td>
                        <td className="border-t border-slate-200 px-2 py-2 text-center bg-blue-50/30">${pr.CC?.minFee?.toFixed(2)}</td>
                        <td className="border-t border-slate-200 px-2 py-2 text-center bg-purple-50/30">
                          {hasAmex ? `${pr.AMEX?.takeRate?.toFixed(2)}%` : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="border-t border-slate-200 px-2 py-2 text-center bg-purple-50/30">
                          {hasAmex ? `$${pr.AMEX?.minFee?.toFixed(2)}` : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="border-t border-slate-200 px-2 py-2 text-center bg-green-50/30">{pr.ACH?.minFee > 0 ? `$${pr.ACH?.minFee?.toFixed(2)}` : <span className="text-slate-300">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Integration Notes */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-slate-600">Integration Requirements</label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={implBulletFormats.integrationNotes}
                    onChange={(e) => setImplBulletFormats(prev => ({ ...prev, integrationNotes: e.target.checked }))}
                    className="w-3 h-3 cursor-pointer"
                  />
                  <span className="text-slate-500">Bullet format</span>
                </label>
              </div>
              {implBulletFormats.integrationNotes && implChecklist.integration_notes && (
                <div className="bg-teal-50 border border-teal-200 rounded p-2 mb-2">
                  <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                    {implChecklist.integration_notes.split('\n').filter(line => line.trim()).map((line, idx) => (
                      <li key={idx}>{line.trim()}</li>
                    ))}
                  </ul>
                </div>
              )}
              <textarea
                value={implChecklist.integration_notes}
                onChange={(e) => setImplChecklist(d => ({ ...d, integration_notes: e.target.value }))}
                className="input-field w-full"
                rows="2"
                placeholder={implBulletFormats.integrationNotes ? "Enter each integration on a new line" : "e.g., Tyler ERP, Harris Software, CivicPlus, Munibilling, etc."}
              />
            </div>

            {/* eStore/Categories */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-slate-600">eStore / Misc / Court: Categories, Products, Payment Options</label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={implBulletFormats.estoreCategories}
                    onChange={(e) => setImplBulletFormats(prev => ({ ...prev, estoreCategories: e.target.checked }))}
                    className="w-3 h-3 cursor-pointer"
                  />
                  <span className="text-slate-500">Bullet format</span>
                </label>
              </div>
              {implBulletFormats.estoreCategories && implChecklist.estore_categories && (
                <div className="bg-teal-50 border border-teal-200 rounded p-2 mb-2">
                  <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                    {implChecklist.estore_categories.split('\n').filter(line => line.trim()).map((line, idx) => (
                      <li key={idx}>{line.trim()}</li>
                    ))}
                  </ul>
                </div>
              )}
              <textarea
                value={implChecklist.estore_categories}
                onChange={(e) => setImplChecklist(d => ({ ...d, estore_categories: e.target.value }))}
                className="input-field w-full"
                rows="3"
                placeholder={implBulletFormats.estoreCategories ? "Enter each category/product on a new line" : "List any special categories, products, or payment options needed..."}
              />
            </div>
          </div>
          )}

          {/* Section 4: Card Reader Info (Auto-populated from CROF) */}
          {isSectionVisible('cardReaderOrder') && (() => {
            const rdrSvcs = (proposal?.scenarios || []).filter(s => s.scenario_info?.readersQty > 0 && s.scenario_info?.readerType && s.scenario_info?.readerType !== 'N/A');
            if (rdrSvcs.length === 0) return null;
            return (
              <div className="mb-8" data-testid="impl-section-4">
                <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
                  <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  Card Reader Order
                  <span className="text-xs font-normal text-slate-400">(Auto-populated from CROF)</span>
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-slate-50 rounded p-3">
                    <label className="text-xs font-semibold text-slate-600 block mb-1">Shipping Address</label>
                    <p className="font-medium text-slate-800">{crofData.shipping_address || '—'}</p>
                  </div>
                  <div className="bg-slate-50 rounded p-3">
                    <label className="text-xs font-semibold text-slate-600 block mb-1">Reader Details</label>
                    <p className="font-medium text-slate-800">
                      {rdrSvcs.map(s => `${s.scenario_info?.readersQty}× ${s.scenario_info?.readerType}`).join(', ') || '—'}
                    </p>
                  </div>
                </div>
                {/* Card Reader Notes */}
                <div className="mt-4">
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Card Reader Notes</label>
                  <textarea
                    value={implChecklist.card_reader_notes || ''}
                    onChange={(e) => setImplChecklist(d => ({ ...d, card_reader_notes: e.target.value }))}
                    className="input-field w-full"
                    rows="2"
                    placeholder="Any special instructions for card reader setup, delivery, or configuration..."
                  />
                </div>
              </div>
            );
          })()}

          {/* Section 5: Required Documents */}
          {isSectionVisible('requiredDocuments') && (
          <div className="mb-8" data-testid="impl-section-5">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">5</span>
              Required Documents <span className="text-red-600">*</span>
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  checked={implChecklist.w9_received}
                  onChange={(e) => setImplChecklist(d => ({ ...d, w9_received: e.target.checked }))}
                  className="w-5 h-5 text-teal-600 rounded"
                />
                <div>
                  <span className="font-medium text-sm text-slate-800">W9 Form</span>
                  <p className="text-xs text-slate-500">Required for tax reporting</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  checked={implChecklist.voided_check_received}
                  onChange={(e) => setImplChecklist(d => ({ ...d, voided_check_received: e.target.checked }))}
                  className="w-5 h-5 text-teal-600 rounded"
                />
                <div>
                  <span className="font-medium text-sm text-slate-800">Voided Check</span>
                  <p className="text-xs text-slate-500">Required for ACH/deposit setup</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  checked={implChecklist.bank_letter_received}
                  onChange={(e) => setImplChecklist(d => ({ ...d, bank_letter_received: e.target.checked }))}
                  className="w-5 h-5 text-teal-600 rounded"
                />
                <div>
                  <span className="font-medium text-sm text-slate-800">Bank Letter</span>
                  <p className="text-xs text-slate-500">Alternative to voided check</p>
                </div>
              </label>
            </div>
            <p className="text-xs text-slate-400 mt-2 italic">Note: Either a Voided Check OR Bank Letter is required for ACH setup.</p>
          </div>
          )}

          {/* Section 6: Special Requests (CRITICAL) */}
          {isSectionVisible('specialRequests') && (
          <div className="mb-8" data-testid="impl-section-6">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-6 h-6 bg-red-100 text-red-700 rounded-full flex items-center justify-center text-xs font-bold">!</span>
              Special Requests / Notes <span className="text-red-600">*</span>
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-semibold">CRITICAL</span>
            </h4>
            <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
              <p className="text-xs text-red-800">
                <strong>This is critical!</strong> We want to set up their services in the way that works for their office. 
                Include anything different from the standard setup:
              </p>
              <ul className="text-xs text-red-700 mt-2 ml-4 list-disc space-y-1">
                {(template?.implementationChecklist?.specialRequestsPrompts || [
                  'Tax commissioner site',
                  'Invoicing system',
                  'Citizen portal / autopay',
                  'Text-to-pay / e-alerts',
                  'Anything outside the "base package"'
                ]).map((prompt, idx) => (
                  <li key={idx}>{prompt}</li>
                ))}
              </ul>
            </div>
            <div className="flex items-center justify-end mb-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={implBulletFormats.specialRequests}
                  onChange={(e) => setImplBulletFormats(prev => ({ ...prev, specialRequests: e.target.checked }))}
                  className="w-3 h-3 cursor-pointer"
                />
                <span className="text-slate-500">Bullet format</span>
              </label>
            </div>
            {implBulletFormats.specialRequests && implChecklist.special_requests && (
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-2">
                <p className="text-xs font-semibold text-red-800 mb-1">Preview (Bulleted Format):</p>
                <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
                  {implChecklist.special_requests.split('\n').filter(line => line.trim()).map((line, idx) => (
                    <li key={idx}>{line.trim()}</li>
                  ))}
                </ul>
              </div>
            )}
            <textarea
              value={implChecklist.special_requests}
              onChange={(e) => setImplChecklist(d => ({ ...d, special_requests: e.target.value }))}
              className="input-field w-full border-2 border-red-200 focus:border-red-400"
              rows="5"
              placeholder={implBulletFormats.specialRequests ? "Enter each special request on a new line (will display as bullets)" : "Describe any special requirements, customizations, or non-standard setup needs..."}
            />
          </div>
          )}

          {/* Section 7: IT & Technical Contact Information */}
          {isSectionVisible('itContact') && (
          <div className="mb-8" data-testid="impl-section-7">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">7</span>
              IT & Technical Contact Information
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">IT Contact Name</label>
                <input
                  type="text"
                  value={implChecklist.it_contact_name}
                  onChange={(e) => setImplChecklist(d => ({ ...d, it_contact_name: e.target.value }))}
                  className="input-field w-full"
                  placeholder="IT department contact"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">IT Contact Email</label>
                <input
                  type="email"
                  value={implChecklist.it_contact_email}
                  onChange={(e) => setImplChecklist(d => ({ ...d, it_contact_email: e.target.value }))}
                  className="input-field w-full"
                  placeholder="it@example.gov"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">IT Contact Phone</label>
                <input
                  type="tel"
                  value={implChecklist.it_contact_phone}
                  onChange={(e) => setImplChecklist(d => ({ ...d, it_contact_phone: e.target.value }))}
                  className="input-field w-full"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Public IP Address</label>
                <input
                  type="text"
                  value={implChecklist.public_ip_address}
                  onChange={(e) => setImplChecklist(d => ({ ...d, public_ip_address: e.target.value }))}
                  className="input-field w-full"
                  placeholder="e.g., 192.168.1.1"
                />
              </div>
            </div>
          </div>
          )}

          {/* Section 8: Billing & Deposit Structure */}
          {isSectionVisible('billingDeposit') && (
          <div className="mb-8" data-testid="impl-section-8">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">8</span>
              Billing & Deposit Structure
            </h4>
            
            {/* Payment Preferences */}
            <div className="grid grid-cols-2 gap-6 mb-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="text-xs font-semibold text-slate-700 mb-3">Payment Acceptance Options</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={implChecklist.allow_partial_payments === true}
                      onChange={(e) => setImplChecklist(d => ({ ...d, allow_partial_payments: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 rounded"
                    />
                    <span className="text-sm text-slate-700">Allow partial payments on bills</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={implChecklist.allow_overpayments === true}
                      onChange={(e) => setImplChecklist(d => ({ ...d, allow_overpayments: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 rounded"
                    />
                    <span className="text-sm text-slate-700">Allow overpayments on bills</span>
                  </label>
                  {/* Custom Payment Options */}
                  {(implChecklist.custom_payment_options || []).map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={opt.enabled === true}
                        onChange={(e) => {
                          const updated = [...(implChecklist.custom_payment_options || [])];
                          updated[idx] = { ...updated[idx], enabled: e.target.checked };
                          setImplChecklist(d => ({ ...d, custom_payment_options: updated }));
                        }}
                        className="w-4 h-4 text-teal-600 rounded"
                      />
                      <input
                        type="text"
                        value={opt.label || ''}
                        onChange={(e) => {
                          const updated = [...(implChecklist.custom_payment_options || [])];
                          updated[idx] = { ...updated[idx], label: e.target.value };
                          setImplChecklist(d => ({ ...d, custom_payment_options: updated }));
                        }}
                        className="flex-1 text-sm border-b border-slate-300 bg-transparent focus:border-teal-500 focus:outline-none px-1 py-0.5"
                        placeholder="Custom option..."
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updated = (implChecklist.custom_payment_options || []).filter((_, i) => i !== idx);
                          setImplChecklist(d => ({ ...d, custom_payment_options: updated }));
                        }}
                        className="text-red-400 hover:text-red-600 text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const updated = [...(implChecklist.custom_payment_options || []), { label: '', enabled: false }];
                      setImplChecklist(d => ({ ...d, custom_payment_options: updated }));
                    }}
                    className="text-xs text-teal-600 hover:text-teal-800 font-medium flex items-center gap-1 mt-2"
                  >
                    + Add Option
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Deposit Frequency</label>
                <select
                  value={implChecklist.deposit_frequency || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, deposit_frequency: e.target.value }))}
                  className="input-field w-full"
                >
                  <option value="">Select frequency...</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="bi-weekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom Schedule</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-600">Deposit Structure Notes</label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={implBulletFormats.depositStructure}
                      onChange={(e) => setImplBulletFormats(prev => ({ ...prev, depositStructure: e.target.checked }))}
                      className="w-3 h-3 cursor-pointer"
                    />
                    <span className="text-slate-500">Bullets</span>
                  </label>
                </div>
                {implBulletFormats.depositStructure && implChecklist.deposit_structure && (
                  <div className="bg-teal-50 border border-teal-200 rounded p-2 mb-2">
                    <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                      {implChecklist.deposit_structure.split('\n').filter(line => line.trim()).map((line, idx) => (
                        <li key={idx}>{line.trim()}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <textarea
                  value={implChecklist.deposit_structure}
                  onChange={(e) => setImplChecklist(d => ({ ...d, deposit_structure: e.target.value }))}
                  className="input-field w-full"
                  rows="3"
                  placeholder={implBulletFormats.depositStructure ? "Enter each item on a new line" : "How should MIDs be configured? Any special deposit requirements..."}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-600">MID Setup Notes</label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={implBulletFormats.midSetupNotes}
                      onChange={(e) => setImplBulletFormats(prev => ({ ...prev, midSetupNotes: e.target.checked }))}
                      className="w-3 h-3 cursor-pointer"
                    />
                    <span className="text-slate-500">Bullets</span>
                  </label>
                </div>
                {implBulletFormats.midSetupNotes && implChecklist.mid_setup_notes && (
                  <div className="bg-teal-50 border border-teal-200 rounded p-2 mb-2">
                    <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                      {implChecklist.mid_setup_notes.split('\n').filter(line => line.trim()).map((line, idx) => (
                        <li key={idx}>{line.trim()}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <textarea
                  value={implChecklist.mid_setup_notes}
                  onChange={(e) => setImplChecklist(d => ({ ...d, mid_setup_notes: e.target.value }))}
                  className="input-field w-full"
                  rows="3"
                  placeholder={implBulletFormats.midSetupNotes ? "Enter each item on a new line" : "Additional MID configuration details..."}
                />
              </div>
            </div>

            {/* Billing Notes */}
            <div className="mt-4">
              <label className="text-xs font-semibold text-slate-600 block mb-1">Billing & Deposit Notes</label>
              <textarea
                value={implChecklist.billing_notes || ''}
                onChange={(e) => setImplChecklist(d => ({ ...d, billing_notes: e.target.value }))}
                className="input-field w-full"
                rows="2"
                placeholder="Any additional billing or deposit-related notes..."
              />
            </div>
          </div>
          )}

          {/* Section 9: Permissions & Operations */}
          {isSectionVisible('permissions') && (
          <div className="mb-8" data-testid="impl-section-9">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">9</span>
              Permissions & Operations
            </h4>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="text-xs font-semibold text-slate-700 mb-3">Clerk Permissions</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={implChecklist.allow_clerk_void === true}
                      onChange={(e) => setImplChecklist(d => ({ ...d, allow_clerk_void: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 rounded"
                    />
                    <span className="text-sm text-slate-700">Allow clerks to void transactions</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={implChecklist.allow_clerk_refund === true}
                      onChange={(e) => setImplChecklist(d => ({ ...d, allow_clerk_refund: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 rounded"
                    />
                    <span className="text-sm text-slate-700">Allow clerks to process refunds</span>
                  </label>
                  {/* Custom Permission Options */}
                  {(implChecklist.custom_permission_options || []).map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={opt.enabled === true}
                        onChange={(e) => {
                          const updated = [...(implChecklist.custom_permission_options || [])];
                          updated[idx] = { ...updated[idx], enabled: e.target.checked };
                          setImplChecklist(d => ({ ...d, custom_permission_options: updated }));
                        }}
                        className="w-4 h-4 text-teal-600 rounded"
                      />
                      <input
                        type="text"
                        value={opt.label || ''}
                        onChange={(e) => {
                          const updated = [...(implChecklist.custom_permission_options || [])];
                          updated[idx] = { ...updated[idx], label: e.target.value };
                          setImplChecklist(d => ({ ...d, custom_permission_options: updated }));
                        }}
                        className="flex-1 text-sm border-b border-slate-300 bg-transparent focus:border-teal-500 focus:outline-none px-1 py-0.5"
                        placeholder="Custom permission..."
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updated = (implChecklist.custom_permission_options || []).filter((_, i) => i !== idx);
                          setImplChecklist(d => ({ ...d, custom_permission_options: updated }));
                        }}
                        className="text-red-400 hover:text-red-600 text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const updated = [...(implChecklist.custom_permission_options || []), { label: '', enabled: false }];
                      setImplChecklist(d => ({ ...d, custom_permission_options: updated }));
                    }}
                    className="text-xs text-teal-600 hover:text-teal-800 font-medium flex items-center gap-1 mt-2"
                  >
                    + Add Permission
                  </button>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="text-xs font-semibold text-slate-700 mb-3">Support Options</p>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={implChecklist.allow_phone_payments === true}
                    onChange={(e) => setImplChecklist(d => ({ ...d, allow_phone_payments: e.target.checked }))}
                    className="w-4 h-4 text-teal-600 rounded"
                  />
                  <span className="text-sm text-slate-700">Allow support agents to take payments over the phone</span>
                </label>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-600">Daily Reports Needed</label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={implBulletFormats.dailyReports}
                      onChange={(e) => setImplBulletFormats(prev => ({ ...prev, dailyReports: e.target.checked }))}
                      className="w-3 h-3 cursor-pointer"
                    />
                    <span className="text-slate-500">Bullets</span>
                  </label>
                </div>
                {implBulletFormats.dailyReports && implChecklist.daily_reports_needed && (
                  <div className="bg-teal-50 border border-teal-200 rounded p-2 mb-2">
                    <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                      {implChecklist.daily_reports_needed.split('\n').filter(line => line.trim()).map((line, idx) => (
                        <li key={idx}>{line.trim()}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <textarea
                  value={implChecklist.daily_reports_needed}
                  onChange={(e) => setImplChecklist(d => ({ ...d, daily_reports_needed: e.target.value }))}
                  className="input-field w-full"
                  rows="3"
                  placeholder={implBulletFormats.dailyReports ? "Enter each report type on a new line" : "List any daily reports needed..."}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-slate-600">Notifications & Alerts Preferences</label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={implBulletFormats.notifications}
                    onChange={(e) => setImplBulletFormats(prev => ({ ...prev, notifications: e.target.checked }))}
                    className="w-3 h-3 cursor-pointer"
                  />
                  <span className="text-slate-500">Bullets</span>
                </label>
              </div>
              {implBulletFormats.notifications && implChecklist.notifications_needed && (
                <div className="bg-teal-50 border border-teal-200 rounded p-2 mb-2">
                  <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                    {implChecklist.notifications_needed.split('\n').filter(line => line.trim()).map((line, idx) => (
                      <li key={idx}>{line.trim()}</li>
                    ))}
                  </ul>
                </div>
              )}
              <textarea
                value={implChecklist.notifications_needed}
                onChange={(e) => setImplChecklist(d => ({ ...d, notifications_needed: e.target.value }))}
                className="input-field w-full"
                rows="2"
                placeholder={implBulletFormats.notifications ? "Enter each notification type on a new line" : "Email alerts, daily summaries, transaction notifications, etc."}
              />
            </div>

            {/* Permissions Notes */}
            <div className="mt-4">
              <label className="text-xs font-semibold text-slate-600 block mb-1">Permissions & Operations Notes</label>
              <textarea
                value={implChecklist.permissions_notes || ''}
                onChange={(e) => setImplChecklist(d => ({ ...d, permissions_notes: e.target.value }))}
                className="input-field w-full"
                rows="2"
                placeholder="Any additional notes about permissions, operations, or reporting requirements..."
              />
            </div>
          </div>
          )}

          {/* Section 10: User Accounts */}
          {isSectionVisible('userAccounts') && (
          <div className="mb-8" data-testid="impl-section-10">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">10</span>
              User Accounts
            </h4>
            
            {/* User Accounts Table */}
            <div className="border-2 border-slate-200 rounded-lg overflow-hidden mb-4">
              <table className="w-full text-xs">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Name</th>
                    <th className="px-3 py-2 text-left font-semibold">Email</th>
                    <th className="px-3 py-2 text-left font-semibold">Role</th>
                    <th className="px-3 py-2 text-left font-semibold">Permissions</th>
                    <th className="px-3 py-2 text-center font-semibold w-16">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(implChecklist.user_accounts || []).map((user, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="border-t border-slate-200 px-3 py-2">
                        <input
                          type="text"
                          value={user.name || ''}
                          onChange={(e) => {
                            const updated = [...(implChecklist.user_accounts || [])];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setImplChecklist(d => ({ ...d, user_accounts: updated }));
                          }}
                          className="input-field w-full text-xs"
                          placeholder="User name"
                        />
                      </td>
                      <td className="border-t border-slate-200 px-3 py-2">
                        <input
                          type="email"
                          value={user.email || ''}
                          onChange={(e) => {
                            const updated = [...(implChecklist.user_accounts || [])];
                            updated[idx] = { ...updated[idx], email: e.target.value };
                            setImplChecklist(d => ({ ...d, user_accounts: updated }));
                          }}
                          className="input-field w-full text-xs"
                          placeholder="email@example.gov"
                        />
                      </td>
                      <td className="border-t border-slate-200 px-3 py-2">
                        <select
                          value={user.role || ''}
                          onChange={(e) => {
                            const updated = [...(implChecklist.user_accounts || [])];
                            updated[idx] = { ...updated[idx], role: e.target.value };
                            setImplChecklist(d => ({ ...d, user_accounts: updated }));
                          }}
                          className="input-field w-full text-xs"
                        >
                          <option value="">Select...</option>
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                          <option value="clerk">Clerk</option>
                          <option value="viewer">View Only</option>
                        </select>
                      </td>
                      <td className="border-t border-slate-200 px-3 py-2">
                        <input
                          type="text"
                          value={user.permissions || ''}
                          onChange={(e) => {
                            const updated = [...(implChecklist.user_accounts || [])];
                            updated[idx] = { ...updated[idx], permissions: e.target.value };
                            setImplChecklist(d => ({ ...d, user_accounts: updated }));
                          }}
                          className="input-field w-full text-xs"
                          placeholder="e.g., Reports, Refunds"
                        />
                      </td>
                      <td className="border-t border-slate-200 px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => {
                            const updated = (implChecklist.user_accounts || []).filter((_, i) => i !== idx);
                            setImplChecklist(d => ({ ...d, user_accounts: updated }));
                          }}
                          className="text-red-500 hover:text-red-700 text-xs"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <button
              type="button"
              onClick={() => {
                const updated = [...(implChecklist.user_accounts || []), { name: '', email: '', role: '', permissions: '' }];
                setImplChecklist(d => ({ ...d, user_accounts: updated }));
              }}
              className="text-sm text-teal-600 hover:text-teal-800 font-medium flex items-center gap-1"
            >
              + Add User Account
            </button>

            <div className="mt-4">
              <label className="text-xs font-semibold text-slate-600 block mb-1">Additional User Account Notes</label>
              <textarea
                value={implChecklist.user_accounts_notes}
                onChange={(e) => setImplChecklist(d => ({ ...d, user_accounts_notes: e.target.value }))}
                className="input-field w-full"
                rows="2"
                placeholder="Any additional notes about user setup requirements..."
              />
            </div>
          </div>
          )}

          {/* Section 11: Branding Assets */}
          {isSectionVisible('brandingAssets') && (
          <div className="mb-8" data-testid="impl-section-11">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">11</span>
              Branding Assets
            </h4>
            
            <div className="grid grid-cols-2 gap-6 mb-4">
              {/* Logo */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-700">Logo / Seal</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={implChecklist.logo_received}
                      onChange={(e) => setImplChecklist(d => ({ ...d, logo_received: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 rounded"
                    />
                    <span className="text-xs text-slate-600">Received</span>
                  </label>
                </div>
                <textarea
                  value={implChecklist.logo_notes || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, logo_notes: e.target.value }))}
                  className="input-field w-full text-xs"
                  rows="2"
                  placeholder="Logo format, dimensions, or request notes..."
                />
              </div>

              {/* Header Image */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-700">Header Image</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={implChecklist.header_image_received}
                      onChange={(e) => setImplChecklist(d => ({ ...d, header_image_received: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 rounded"
                    />
                    <span className="text-xs text-slate-600">Received</span>
                  </label>
                </div>
                <textarea
                  value={implChecklist.header_image_notes || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, header_image_notes: e.target.value }))}
                  className="input-field w-full text-xs"
                  rows="2"
                  placeholder="Header image requirements or notes..."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Brand Colors</label>
                <input
                  type="text"
                  value={implChecklist.branding_colors || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, branding_colors: e.target.value }))}
                  className="input-field w-full"
                  placeholder="e.g., Primary: #1E3A5F, Accent: #4A90D9"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Additional Branding Notes</label>
                <input
                  type="text"
                  value={implChecklist.branding_notes || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, branding_notes: e.target.value }))}
                  className="input-field w-full"
                  placeholder="Font preferences, style guide, etc."
                />
              </div>
            </div>
          </div>
          )}

          {/* Section 12: Training & Onboarding */}
          {isSectionVisible('trainingOnboarding') && (
          <div className="mb-8" data-testid="impl-section-12">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">12</span>
              Training & Onboarding
            </h4>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-700">Training Scheduled</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={implChecklist.training_scheduled}
                      onChange={(e) => setImplChecklist(d => ({ ...d, training_scheduled: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 rounded"
                    />
                    <span className="text-xs text-slate-600">Confirmed</span>
                  </label>
                </div>
                {implChecklist.training_scheduled && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Training Date</label>
                      <input
                        type="date"
                        value={implChecklist.training_date || ''}
                        onChange={(e) => setImplChecklist(d => ({ ...d, training_date: e.target.value }))}
                        className="input-field w-full text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Training Format</label>
                      <select
                        value={implChecklist.training_format || ''}
                        onChange={(e) => setImplChecklist(d => ({ ...d, training_format: e.target.value }))}
                        className="input-field w-full text-xs"
                      >
                        <option value="">Select format...</option>
                        <option value="in-person">In-Person</option>
                        <option value="remote">Remote (Video Call)</option>
                        <option value="hybrid">Hybrid</option>
                        <option value="self-service">Self-Service (Documentation)</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Training Attendees</label>
                <textarea
                  value={implChecklist.training_attendees || ''}
                  onChange={(e) => setImplChecklist(d => ({ ...d, training_attendees: e.target.value }))}
                  className="input-field w-full text-xs"
                  rows="4"
                  placeholder="List names/emails of people who will attend training..."
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200 cursor-pointer hover:bg-green-100 transition-colors">
                <input
                  type="checkbox"
                  checked={implChecklist.support_contact_provided}
                  onChange={(e) => setImplChecklist(d => ({ ...d, support_contact_provided: e.target.checked }))}
                  className="w-5 h-5 text-green-600 rounded"
                />
                <div>
                  <span className="font-medium text-sm text-slate-800">Support Contact Provided</span>
                  <p className="text-xs text-slate-500">Customer has been given support contact information</p>
                </div>
              </label>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Training Notes</label>
              <textarea
                value={implChecklist.training_notes || ''}
                onChange={(e) => setImplChecklist(d => ({ ...d, training_notes: e.target.value }))}
                className="input-field w-full"
                rows="2"
                placeholder="Any special training requirements, topics to cover, etc."
              />
            </div>
          </div>
          )}

          {/* End of Implementation Checklist marker */}
          <div className="mt-6 border-t-4 border-double border-slate-900 pt-3" data-testid="end-of-document-marker">
            <div className="bg-teal-50 px-4 py-3 rounded text-center">
              <span className="text-xs font-bold text-teal-700 tracking-wider">END OF AGREEMENT 4 OF 4 — END OF DOCUMENT PACKAGE</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-xs text-center text-slate-500 border-t pt-4 mt-8">
          {template.footer.copyright}
        </div>

        {/* Save Reminder */}
        {!agreement && (
          <div className="mt-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
            <p className="text-sm text-yellow-900">
              <strong>Remember to save:</strong> Click "Save Agreement" before downloading PDF
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function getDefaultTemplate() {
  return {
    header: {
      logo: 'GOVERNMENT WINDOW',
      title: 'No Term, Non-Exclusive SaaS Agreement'
    },
    principal: {
      name: 'Government Window, LLC',
      address: '125 Townpark Drive NW. Suite 500, Kennesaw GA 30144'
    },
    grant: {
      statement: 'This agreement sets out the terms upon which the Principal grants the use of certain software applications referred to as the Licensed Items, by way of Software as a Service ("SaaS"), to the End-User listed below.'
    },
    scheduleA: {
      intro: 'Schedule "A" lists the service fees for each Module ("Licensed Items") selected below.',
      functionInstruction: 'Check the applicable function(s) per Module using the IVR, WEB, and POS columns. [IVR] = Interactive Voice Response, [WEB] = Web Pay, [POS] = Point of Sale. eCheck/ACH is not applicable for POS transactions.',
      columns: [
        { id: 'module', label: 'Module', visible: true },
        { id: 'function', label: 'Function', visible: true },
        { id: 'reader_type', label: 'Reader Type', visible: true },
        { id: 'reader_qty', label: 'Reader Qty', visible: true },
        { id: 'cc_rate', label: 'CC Rate', visible: true },
        { id: 'cc_min', label: 'CC Min', visible: true },
        { id: 'amex_rate', label: 'AmEx Rate', visible: true },
        { id: 'amex_min', label: 'AmEx Min', visible: true },
        { id: 'echeck', label: 'E-check', visible: true }
      ]
    },
    termsAndConditions: {
      content: ''
    },
    stipulations: {
      costAllocation: 'Cost allocation fees based on a percentage of each transaction amount and/or a minimum fee per transaction.'
    },
    footer: {
      copyright: '© 2025'
    }
  };
}
